import { Minus, Plus, Shirt, X } from 'lucide-react'
import { useCart } from './cart-context'
import { useWhatsAppCart } from './use-whatsapp-cart'
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '@/components/ui/sheet'
import { Button } from '@/components/ui/button'
import { EmptyState } from '@/components/shared/EmptyState'
import { WhatsAppPricingButtons } from '@/components/shared/WhatsAppPricingButtons'

interface CartDrawerProps {
  container?: HTMLElement | null
}

export function CartDrawer({ container }: CartDrawerProps) {
  const { items, isOpen, closeCart, updateQuantity, removeItem, clearCart } = useCart()
  const { openWhatsApp } = useWhatsAppCart()

  return (
    <Sheet open={isOpen} onOpenChange={(open) => !open && closeCart()}>
      <SheetContent container={container} className="flex w-full flex-col gap-0 sm:max-w-md">
        <SheetHeader className="border-b">
          <SheetTitle>Tu lista</SheetTitle>
          <SheetDescription>Revisa los productos y escríbenos para confirmar disponibilidad.</SheetDescription>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto p-4">
          {items.length === 0 ? (
            <EmptyState title="Tu lista está vacía" description="Agrega productos desde el catálogo." />
          ) : (
            <div className="grid gap-3">
              {items.map((item) => (
                <div key={item.productId} className="flex gap-3 rounded-lg border p-3">
                  <div className="size-16 shrink-0 overflow-hidden rounded-md bg-muted">
                    {item.imageUrl ? (
                      <img src={item.imageUrl} alt={item.name} className="size-full object-cover" />
                    ) : (
                      <div className="flex size-full items-center justify-center text-muted-foreground/50">
                        <Shirt className="size-6" />
                      </div>
                    )}
                  </div>

                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium text-foreground">{item.name}</p>
                    <div className="mt-2 flex items-center gap-3 rounded-md border border-border px-2 py-1 w-fit">
                      <button
                        type="button"
                        onClick={() => updateQuantity(item.productId, item.quantity - 1)}
                        aria-label="Disminuir cantidad"
                        className="text-muted-foreground hover:text-foreground"
                      >
                        <Minus className="size-3.5" />
                      </button>
                      <span className="w-4 text-center text-sm tabular-nums">{item.quantity}</span>
                      <button
                        type="button"
                        onClick={() => updateQuantity(item.productId, item.quantity + 1)}
                        aria-label="Aumentar cantidad"
                        className="text-muted-foreground hover:text-foreground"
                      >
                        <Plus className="size-3.5" />
                      </button>
                    </div>
                  </div>

                  <button
                    type="button"
                    onClick={() => removeItem(item.productId)}
                    aria-label={`Quitar ${item.name}`}
                    className="text-muted-foreground hover:text-destructive"
                  >
                    <X className="size-4" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {items.length > 0 && (
          <div className="grid gap-3 border-t p-4">
            <WhatsAppPricingButtons onSelect={openWhatsApp} />
            <Button variant="ghost" size="sm" onClick={clearCart}>
              Vaciar lista
            </Button>
          </div>
        )}
      </SheetContent>
    </Sheet>
  )
}
